import getPieveAuthToken from '@/lib/auth/getPieveAuthToken.js';
import getPieveCompanyIdForRequest from '@/lib/auth/getPieveCompanyIdForRequest.js';
import getPieveApiBaseUrl from '@/lib/getPieveApiBaseUrl.js';
import { store } from '@/redux/lib/configureStore.js';
import dispatchHelper from '@/redux/requests/dispatchHelper.js';
import buildOptimisticMutationMeta from '@/redux/requests/lib/buildOptimisticMutationMeta.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';
import setRequestLoaderState from '@/redux/requests/requestLoader.js';

const GET_DEDUPE_IN_FLIGHT_BY_KEY = new Map();
const GET_DEDUPE_RESULT_CACHE_BY_KEY = new Map();
const GET_DEDUPE_RESULT_TTL_MS = 1500;
let GET_DEDUPE_CACHE_EPOCH = 0;

const cloneCachedResult = (value) => {
  if (value === null || value === undefined) {
    return value;
  }

  try {
    if (typeof globalThis?.structuredClone === 'function') {
      return globalThis.structuredClone(value);
    }
  } catch {
    // no-op
  }

  if (Array.isArray(value)) {
    return value.map((item) => cloneCachedResult(item));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        return [key, cloneCachedResult(item)];
      })
    );
  }

  return value;
};

const invalidateApiRequestGetDedupeState = () => {
  GET_DEDUPE_CACHE_EPOCH += 1;
  GET_DEDUPE_IN_FLIGHT_BY_KEY.clear();
  GET_DEDUPE_RESULT_CACHE_BY_KEY.clear();
};

export const __resetApiRequestCacheForTests = () => {
  invalidateApiRequestGetDedupeState();
};

const toRequestUrl = (url, query = {}) => {
  const baseUrl = getPieveApiBaseUrl();
  const fixedUrl = String(url || '').match(/^https*:\/\//) ? String(url) : `${baseUrl}${String(url || '').startsWith('/') ? '' : '/'}${String(url || '')}`;
  const requestUrl = fixedUrl.match(/^https*:\/\//) ? new URL(fixedUrl) : new URL(fixedUrl, window.location.origin);

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    requestUrl.searchParams.set(key, String(value));
  });

  return requestUrl.toString();
};

const buildHeaders = async ({ authToken, headers = {}, data, resolveAuthToken = true, companyId } = {}) => {
  const token = resolveAuthToken !== false && (authToken === undefined || authToken === null || authToken === '') ? await getPieveAuthToken() : authToken;
  const requestCompanyId = companyId === undefined ? getPieveCompanyIdForRequest() : companyId;
  const nextHeaders = {
    ...(data !== undefined && !(data instanceof FormData)
      ? {
          'Content-Type': 'application/json'
        }
      : {}),
    ...(headers || {})
  };

  if (token) {
    nextHeaders.Authorization = `Bearer ${String(token)}`;
  }

  /* The web app authenticates with the session cookie (no token), desktop and Teams with a bearer
     token; either way the request names the organization it acts in. */
  if (requestCompanyId) {
    nextHeaders['x-company-id'] = requestCompanyId;
  }

  return nextHeaders;
};

const getGetDedupeKey = ({ method, url, authToken, resolveAuthToken, responseType, companyId } = {}) => {
  if (
    String(method || '')
      .trim()
      .toUpperCase() !== 'GET'
  ) {
    return '';
  }

  return JSON.stringify({
    url: String(url || ''),
    authToken: authToken === undefined ? '__AUTO__' : authToken,
    companyId: companyId || null,
    resolveAuthToken: resolveAuthToken !== false,
    responseType: responseType || null
  });
};

// `authToken: false` marks a public request: it names no organization, with or without a session cookie.
const shouldResolveRequestCompanyId = ({ authToken, resolveAuthToken } = {}) => {
  return authToken !== false && (resolveAuthToken !== false || Boolean(authToken));
};

const hasRequestCompanyScopeChanged = ({ method, requestCompanyId } = {}) => {
  if (
    String(method || '')
      .trim()
      .toUpperCase() !== 'GET'
  ) {
    return false;
  }

  const normalizedRequestCompanyId = String(requestCompanyId || '').trim();

  if (!normalizedRequestCompanyId) {
    return false;
  }

  const currentCompanyId = String(getPieveCompanyIdForRequest() || '').trim();
  return currentCompanyId !== normalizedRequestCompanyId;
};

const readCachedGetResult = ({ dedupeKey, epoch } = {}) => {
  if (!dedupeKey || !Number.isFinite(epoch)) {
    return null;
  }

  const cachedEntry = GET_DEDUPE_RESULT_CACHE_BY_KEY.get(dedupeKey);

  if (!cachedEntry) {
    return null;
  }

  if (cachedEntry.epoch !== epoch || cachedEntry.expiresAt <= Date.now()) {
    GET_DEDUPE_RESULT_CACHE_BY_KEY.delete(dedupeKey);
    return null;
  }

  return cloneCachedResult(cachedEntry.result);
};

const writeCachedGetResult = ({ dedupeKey, result, epoch } = {}) => {
  if (!dedupeKey || !Number.isFinite(epoch) || !result || result.success !== true) {
    return;
  }

  if (epoch !== GET_DEDUPE_CACHE_EPOCH) {
    return;
  }

  GET_DEDUPE_RESULT_CACHE_BY_KEY.set(dedupeKey, {
    epoch,
    expiresAt: Date.now() + GET_DEDUPE_RESULT_TTL_MS,
    result: cloneCachedResult(result)
  });
};

const parseResponseBody = async ({ response, responseType } = {}) => {
  if (responseType === 'arrayBuffer' && response?.ok) {
    return {
      arrayBuffer: await response.arrayBuffer().catch(() => null),
      mimeType: response.headers?.get?.('content-type') || null,
      contentDisposition: response.headers?.get?.('content-disposition') || null
    };
  }

  const rawBody = await response.text().catch(() => '');

  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return rawBody;
  }
};

const toResponsePayload = ({ response, body } = {}) => {
  if (body && typeof body === 'object' && typeof body.success === 'boolean') {
    if (body.success === false) {
      return {
        ...body,
        status: response?.status || body.status || null
      };
    }

    return body;
  }

  if (response?.ok) {
    return {
      success: true,
      data: body && typeof body === 'object' ? body : {}
    };
  }

  const failure = createRequestFailure({
    message: body && typeof body === 'object' ? String(body?.message || response?.statusText || 'Request failed') : typeof body === 'string' && body.trim() ? body : String(response?.statusText || 'Request failed'),
    status: response?.status || null,
    data: {
      body
    }
  });

  // The server's error code (for example REVERIFICATION_REQUIRED or INVALID_CREDENTIALS) rides at the top level.
  return body?.code ? { ...failure, code: body.code } : failure;
};

const handleAPIRequest = async (config = {}) => {
  const actionTypes = Array.isArray(config.action) ? config.action.filter(Boolean) : config.action ? [config.action] : [];
  const normalizedMethod = String(config.method || 'GET')
    .trim()
    .toUpperCase();
  const resolvedMeta = config.action && normalizedMethod !== 'GET' ? buildOptimisticMutationMeta({ meta: config.meta }) : config.meta;
  const dispatches = actionTypes.map((actionType) => dispatchHelper(actionType, store.dispatch));
  const requestCompanyId = shouldResolveRequestCompanyId({
    authToken: config.authToken,
    resolveAuthToken: config.resolveAuthToken
  })
    ? config.companyId === undefined
      ? getPieveCompanyIdForRequest()
      : config.companyId
    : null;
  const handleDispatch = (response) => {
    if (dispatches.length > 0) {
      dispatches.forEach((dispatchResult) => dispatchResult(response, config.callback, resolvedMeta, config.serverCallback));
      return;
    }

    if (typeof config.callback === 'function') {
      const payload = response?.data;
      const isError = !response?.status || response.status >= 400 || payload?.success === false;
      config.callback(null, isError ? undefined : payload, isError ? payload : undefined);
    }

    if (typeof config.serverCallback === 'function') {
      const payload = response?.data;
      const isError = !response?.status || response.status >= 400 || payload?.success === false;
      config.serverCallback(null, isError ? undefined : payload, isError ? payload : undefined);
    }
  };

  if (config.cancelRef?.current && typeof config.cancelRef.current.abort === 'function') {
    config.cancelRef.current.abort();
    config.cancelRef.current = undefined;
  }

  const abortController = config.cancelRef ? new AbortController() : null;

  if (config.cancelRef) {
    config.cancelRef.current = abortController;
  }

  if (config.loader) {
    setRequestLoaderState({
      key: config.loader,
      isLoading: true
    });
  }

  if (config.action && normalizedMethod !== 'GET') {
    const optimisticPayload = resolvedMeta ? { ...(config.data || {}), meta: resolvedMeta } : config.data || null;
    store.dispatch({ type: `${config.action}_OPTIMISTIC`, payload: optimisticPayload });
  }
  const canUseGetDedupe = normalizedMethod === 'GET' && config.dedupeEligible === true;
  const requestEpoch = GET_DEDUPE_CACHE_EPOCH;
  const dedupeKey = canUseGetDedupe
    ? getGetDedupeKey({
        method: normalizedMethod,
        url: config.url,
        authToken: config.authToken,
        companyId: requestCompanyId,
        resolveAuthToken: config.resolveAuthToken,
        responseType: config.responseType
      })
    : '';

  if (normalizedMethod !== 'GET') {
    invalidateApiRequestGetDedupeState();
  } else if (canUseGetDedupe) {
    const cachedResult = readCachedGetResult({
      dedupeKey,
      epoch: requestEpoch
    });

    if (cachedResult) {
      return cachedResult;
    }

    const inFlightEntry = GET_DEDUPE_IN_FLIGHT_BY_KEY.get(dedupeKey);

    if (inFlightEntry?.epoch === requestEpoch) {
      return cloneCachedResult(await inFlightEntry.promise);
    }
  }

  const requestPromise = (async () => {
    const isRequestAborted = () => {
      return abortController?.signal?.aborted === true || config.signal?.aborted === true;
    };

    try {
      /* A mutation may be told to leave AFTER another one has answered (`sendAfter`, a promise
         of that answer): its optimistic dispatch above already happened, so the screen shows it
         now, and only the wire waits, for a server that must see the two in order (an undo,
         then its redo). A falsy answer (the earlier one was refused, or never answered) means
         this one must not leave either: it settles here as failed, without a wire round trip,
         so the screen rolls it back like any refusal. */
      if (config.sendAfter && typeof config.sendAfter.then === 'function') {
        const previousAnswer = await config.sendAfter.then(
          (answer) => answer,
          () => null
        );

        if (!previousAnswer) {
          const responseShape = {
            status: null,
            data: createRequestFailure({
              code: 'PREVIOUS_REQUEST_FAILED',
              message: 'A change this one builds on did not go through.',
              status: null
            }),
            config: {
              url: config.url
            }
          };

          handleDispatch(responseShape);
          return responseShape.data;
        }
      }

      const headers = await buildHeaders({
        authToken: config.authToken,
        headers: config.headers,
        data: config.data,
        resolveAuthToken: config.resolveAuthToken,
        companyId: requestCompanyId
      });
      const response = await fetch(config.url, {
        method: config.method,
        headers,
        // The session cookie travels with every request unless a caller opts out (public embeds).
        credentials: config.credentials || 'include',
        ...(config.data !== undefined
          ? {
              body: config.data instanceof FormData ? config.data : JSON.stringify(config.data)
            }
          : {}),
        ...(abortController
          ? {
              signal: config.signal || abortController.signal
            }
          : config.signal
            ? {
                signal: config.signal
              }
            : {})
      });

      if (isRequestAborted()) {
        return null;
      }

      const body = await parseResponseBody({
        response,
        responseType: config.responseType
      });

      if (isRequestAborted()) {
        return null;
      }

      if (
        hasRequestCompanyScopeChanged({
          method: normalizedMethod,
          requestCompanyId
        })
      ) {
        return null;
      }

      let responseShape = {
        status: response.status,
        responseHeaders: {
          etag: response.headers?.get?.('etag') || null
        },
        data: toResponsePayload({
          response,
          body
        }),
        rawBody: body,
        config: {
          url: config.url
        }
      };

      if (typeof config.onResponse === 'function') {
        responseShape = config.onResponse(responseShape) || responseShape;
      }

      handleDispatch(responseShape);

      if (config.eventKey && responseShape.data?.success !== false) {
        store.dispatch({ type: 'uiSetEventKey', payload: { key: config.eventKey, timestamp: Date.now() } });
      }

      return responseShape.data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        return null;
      }

      const responseShape = {
        status: null,
        data: createRequestFailure({
          code: 'NETWORK_ERROR',
          message: 'Network request failed.',
          status: null,
          detail: err?.message || String(err || '')
        }),
        config: {
          url: config.url
        }
      };

      handleDispatch(responseShape);
      return responseShape.data;
    } finally {
      if (config.cancelRef?.current === abortController) {
        config.cancelRef.current = undefined;
      }

      if (config.loader) {
        setRequestLoaderState({
          key: config.loader,
          isLoading: false
        });
      }
    }
  })();

  if (canUseGetDedupe) {
    GET_DEDUPE_IN_FLIGHT_BY_KEY.set(dedupeKey, {
      epoch: requestEpoch,
      promise: requestPromise
    });
  }

  try {
    const result = await requestPromise;

    if (canUseGetDedupe) {
      writeCachedGetResult({
        dedupeKey,
        result,
        epoch: requestEpoch
      });
    }

    return result;
  } finally {
    if (canUseGetDedupe) {
      const currentInFlightRequest = GET_DEDUPE_IN_FLIGHT_BY_KEY.get(dedupeKey);

      if (currentInFlightRequest?.promise === requestPromise) {
        GET_DEDUPE_IN_FLIGHT_BY_KEY.delete(dedupeKey);
      }
    }
  }
};

const apiRequest = (method, url, config = {}) => {
  const requestUrl = toRequestUrl(url, config.query);
  const dedupeEligible =
    String(method || 'GET')
      .trim()
      .toUpperCase() === 'GET' &&
    !config.signal &&
    !config.cancelRef &&
    !config.action &&
    typeof config.callback !== 'function' &&
    typeof config.serverCallback !== 'function' &&
    !config.loader;

  return new Promise(async (resolve) => {
    const payload = await handleAPIRequest({
      ...config,
      dedupeEligible,
      callback: (nothing, data, err) => {
        resolve(data || err || null);

        if (typeof config.callback === 'function') {
          config.callback(data, err);
        }
      },
      serverCallback: (nothing, data, err) => {
        if (typeof config.serverCallback === 'function') {
          config.serverCallback(data, err);
        }
      },
      method,
      url: requestUrl
    });

    if (config.action || typeof config.callback === 'function') {
      resolve(payload || null);
      return;
    }

    resolve(payload);
  });
};

export default apiRequest;
export { invalidateApiRequestGetDedupeState };
